import type { ToolId } from "./tools";

export interface GenerationHistoryItem {
  id: string;
  tool: ToolId;
  toolTitle: string;
  createdAt: string;
  productName: string;
  output: Record<string, string | string[]>;
  model?: string;
}

const HISTORY_KEY = "eg:generation-history";
const ONBOARDING_KEY = "eg:onboarding";
const SHOP_KEY = "eg:shop-input";

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function loadHistory(): GenerationHistoryItem[] {
  if (!isBrowser()) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as GenerationHistoryItem[]) : [];
  } catch {
    return [];
  }
}

export function saveHistory(items: GenerationHistoryItem[]): void {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.setItem(HISTORY_KEY, JSON.stringify(items.slice(0, 20)));
}

export function addHistoryItem(item: GenerationHistoryItem): GenerationHistoryItem[] {
  const next = [item, ...loadHistory()].slice(0, 20);
  saveHistory(next);
  return next;
}

export function onboardingComplete(): boolean {
  if (!isBrowser()) {
    return false;
  }

  return window.localStorage.getItem(ONBOARDING_KEY) === "done";
}

export function saveOnboarding(data: Record<string, string | boolean>): void {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.setItem("eg:onboarding-data", JSON.stringify(data));
  window.localStorage.setItem(ONBOARDING_KEY, "done");
}

export function loadOnboardingData(): Record<string, string | boolean> {
  if (!isBrowser()) {
    return {};
  }

  try {
    return JSON.parse(window.localStorage.getItem("eg:onboarding-data") ?? "{}") as Record<string, string | boolean>;
  } catch {
    return {};
  }
}

export function loadShopInput(): string {
  if (!isBrowser()) {
    return "";
  }

  return window.localStorage.getItem(SHOP_KEY) ?? "";
}

export function saveShopInput(value: string): void {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.setItem(SHOP_KEY, value);
}

export function makeHistoryId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `generation-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
